import React, { useEffect, useMemo, useState, useCallback ,useRef} from "react";
import {
  Hourglass,
  ShieldAlert,
  RefreshCw,
  CheckCircle2,
  Filter as FilterIcon,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  TrendingUp,
  Timer,
  Clock,
  ClipboardList,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/themeContext";
import { useAuth } from "../../context/authContext";
import {
  BRAND,
  TOKENS,
  CardShell,
  useIsMobile,
  STATUS_META,
  PAGE_SIZE,
  categoryTint,
  fmtDate,
  apiRequest,
  StatusBadge,
  PriorityBadge,
  CategoryTag,
  Toast,
} from "./GrievanceShared";

// some helper functions
function getPathname(url) {
  try {
    return new URL(url, window.location.origin).pathname.replace(/\/+$/, "");
  } catch {
    return url ?? "";
  }
}

// ---------------------------------------------------------------------------
// Section 1 — Dashboard statistics
// ---------------------------------------------------------------------------

function StatCard({ label, value, icon: Icon, tint, accent, hint }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <CardShell accent={accent}>
      <div className="flex items-center justify-between">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-full"
          style={{ backgroundColor: `${tint}22`, color: tint }}
        >
          <Icon size={18} strokeWidth={2} />
        </span>
        {hint && (
          <span
            className={
              "rounded-full border px-2.5 py-1 text-[10px] font-medium " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            {hint}
          </span>
        )}
      </div>
      <div
        className={
          "mt-4 text-3xl sm:text-4xl font-extrabold leading-none " +
          t.textPrimary
        }
      >
        {value}
      </div>
      <div className={"mt-2 text-xs sm:text-sm font-medium " + t.textMuted}>
        {label}
      </div>
    </CardShell>
  );
}

function DashboardStats({ stats }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 md:grid-cols-4">
      <StatCard
        label="Pending for  review"
        value={stats.pending}
        icon={Hourglass}
        tint={BRAND.gold}
        accent="gold"
      />
      <StatCard
        label="Escalated  "
        value={stats.escalated}
        icon={ShieldAlert}
        tint={BRAND.red}
        accent="red"
      />
      <StatCard
        label="In-progress "
        value={stats.inProgress}
        icon={RefreshCw}
        tint={BRAND.purple}
        accent="purple"
      />
      <StatCard
        label="Resolved  "
        value={stats.resolvedThisMonth}
        icon={CheckCircle2}
        tint={BRAND.teal}
        accent="teal"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 4 — Analytics cards
// ---------------------------------------------------------------------------

function AnalyticsCards({ resolutionRate, avgResolutionDays }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const pct = Math.max(0, Math.min(100, resolutionRate ?? 0));

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2">
      <CardShell accent="teal">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div
              className={
                "flex items-center gap-2 text-xs font-semibold uppercase tracking-wide " +
                t.textMuted
              }
            >
              <TrendingUp size={14} className="text-[#34d9b8]" />
              Resolution Rate
            </div>
            <div
              className={
                "mt-2 text-3xl sm:text-4xl font-extrabold " + t.textPrimary
              }
            >
              {Math.round(pct)}%
            </div>
            <p className={"mt-1 text-xs sm:text-sm " + t.textMuted}>
              Share of filed grievances closed out by the team
            </p>
          </div>
          <div className="relative h-16 w-16 shrink-0 sm:h-20 sm:w-20">
            <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke={t.gridStroke}
                strokeWidth="3.5"
              />
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke={BRAND.teal}
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeDasharray={`${(pct / 100) * 97.4} 97.4`}
              />
            </svg>
          </div>
        </div>
      </CardShell>

      <CardShell accent="purple">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div
              className={
                "flex items-center gap-2 text-xs font-semibold uppercase tracking-wide " +
                t.textMuted
              }
            >
              <Timer size={14} className="text-[#8b7cf6]" />
              Avg. Resolution Time
            </div>
            <div
              className={
                "mt-2 flex items-baseline gap-1.5 text-3xl sm:text-4xl font-extrabold " +
                t.textPrimary
              }
            >
              {avgResolutionDays?.toFixed
                ? avgResolutionDays.toFixed(1)
                : avgResolutionDays}
              <span className={"text-base font-semibold " + t.textFaint}>
                days
              </span>
            </div>
            <p className={"mt-1 text-xs sm:text-sm " + t.textMuted}>
              From filing to case closure, across resolved cases
            </p>
          </div>
          <span
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full sm:h-16 sm:w-16"
            style={{
              backgroundColor: `${BRAND.purple}1f`,
              color: BRAND.purple,
            }}
          >
            <Clock size={26} strokeWidth={2} />
          </span>
        </div>
      </CardShell>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Filter bar
// ---------------------------------------------------------------------------

function FilterBar({ counts, active, onChange, search, onSearch }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  const filters = [
    { key: "all", label: "All", tint: BRAND.silver },
    ...Object.entries(STATUS_META).map(([key, meta]) => ({
      key,
      label: meta.label,
      tint: meta.tint,
    })),
  ];

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => {
          const isActive = active === f.key;
          return (
            <button
              key={f.key}
              onClick={() => onChange(f.key)}
              className={
                "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-200 hover:-translate-y-0.5 " +
                (isActive
                  ? "text-white"
                  : t.pillBorder +
                    " " +
                    t.surfaceSoft +
                    " " +
                    t.textMuted +
                    " " +
                    t.surfaceHover)
              }
              style={
                isActive
                  ? { backgroundColor: f.tint, borderColor: f.tint }
                  : undefined
              }
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: isActive ? "#fff" : f.tint }}
              />
              {f.label}
              <span
                className={
                  "ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold " +
                  (isActive ? "bg-white/25" : t.surface)
                }
              >
                {counts[f.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <label
        className={
          "flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs sm:min-w-[220px] " +
          t.pillBorder +
          " " +
          t.surfaceSoft
        }
      >
        <Search size={14} className={t.textFaint} />
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search by Grievance ID…"
          className={
            "w-full bg-transparent outline-none placeholder:" +
            t.textFaint +
            " " +
            t.textPrimary
          }
        />
        {search && (
          <button
            onClick={() => onSearch("")}
            className={t.textFaint}
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        )}
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

function Pagination({ pagination, onPageChange }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { page, limit, total } = pagination;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (total === 0) return null;

  const start = (page - 1) * limit + 1;
  const end = Math.min(total, page * limit);

  return (
    <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
      <p className={"text-[11px] sm:text-xs " + t.textFaint}>
        Showing{" "}
        <span className={"font-semibold " + t.textMuted}>
          {start}–{end}
        </span>{" "}
        of <span className={"font-semibold " + t.textMuted}>{total}</span>{" "}
        grievances
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className={
            "flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.surfaceHover +
            " " +
            t.textMuted
          }
        >
          <ChevronLeft size={13} /> Prev
        </button>
        <span className={"text-xs font-medium " + t.textMuted}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className={
            "flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.surfaceHover +
            " " +
            t.textMuted
          }
        >
          Next <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Grievances table (collapses to stacked cards on mobile)
// ---------------------------------------------------------------------------

function GrievancesTable({ rows, loading, error, onOpen, setPage }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const isMobile = useIsMobile(768);


  const rowRefs = useRef(new Map());
  const [highlightedTest, setHighlightedTest] = useState(null);
  const allGrievancesRef = useRef(rows);
  allGrievancesRef.current = rows;

  const findAndHighlight = useCallback(
    (redirectTo) => {
      if (!redirectTo) return;

      const targetPath = getPathname(redirectTo);
      const list = allGrievancesRef.current;
      console.log(targetPath)
     

      // 1. Match targetPath against grievance ID, grievanceId, or link
      const index = list.findIndex((g) => {
        const id = String(g.id || g.grievanceId || "");
        const linkPath = getPathname(g.link || "");
        return (
          id === targetPath ||
          linkPath === targetPath ||
          targetPath.endsWith(id)
        );
      });

      if (index === -1) return;

      // 2. Calculate target page (1-indexed)
      const targetPage = Math.floor(index / PAGE_SIZE) + 1;
      const targetGrievance = list[index];
      const targetKey = targetGrievance.id || targetGrievance.grievanceId;

      if (setPage) setPage(targetPage);

      // 3. Scroll & trigger highlight animation
      requestAnimationFrame(() => {
        setTimeout(() => {
          const element = rowRefs.current.get(targetKey);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            setHighlightedTest(targetKey);
            setTimeout(() => setHighlightedTest(null), 2000);
          }
        }, 100);
      });
    },
    [setPage]
  );

  // Handle same-page event dispatching
  useEffect(() => {
    const handler = (e) => findAndHighlight(e.detail?.redirectTo);
    window.addEventListener("ta-highlight-test", handler);
    return () => window.removeEventListener("ta-highlight-test", handler);
  }, [findAndHighlight]);

  // Handle cross-page navigation from sessionStorage
  useEffect(() => {
    if (rows.length === 0) return;
    const pending = sessionStorage.getItem("pendingHighlightGrievance");
    if (pending) {
      findAndHighlight(pending);
      sessionStorage.removeItem("pendingHighlightGrievance");
    }
  }, [rows, findAndHighlight]);

  if (loading) {
    return (
      <div className={"flex h-40 items-center justify-center gap-2 rounded-2xl text-sm " + t.surfaceSoft + " " + t.textMuted}>
        <RefreshCw size={15} className="animate-spin" />
        Loading grievances…
      </div>
    );
  }

  if (error) {
    return (
      <div className={"flex h-40 flex-col items-center justify-center gap-1.5 rounded-2xl text-sm " + t.surfaceSoft + " " + t.textMuted}>
        Couldn't load grievances right now.
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={"flex flex-col items-center gap-2 rounded-2xl px-4 py-12 text-center " + t.surfaceSoft}>
        <ClipboardList size={26} className={t.textFaint} />
        <p className={"text-sm font-medium " + t.textMuted}>No grievances match this filter.</p>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-2.5">
        {rows.map((g) => {
          const keyId = g.id || g.grievanceId;
          const isHighlighted = highlightedTest === keyId;
          return (
            <button
              key={keyId}
              ref={(el) => {
                if (el) rowRefs.current.set(keyId, el);
              }}
              onClick={() => onOpen(g.id)}
              className={
                "w-full rounded-2xl border p-4 text-left transition-all duration-300 " +
                t.rowBorder + " " + t.surfaceSoft + " " + t.surfaceHover +
                (isHighlighted ? " ta-highlight-flash" : "")
              }
            >
              <div className="flex items-center justify-between gap-2">
                <span className={"text-sm font-bold " + t.textPrimary}>{g.id}</span>
                <StatusBadge status={g.status} size="sm" />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <CategoryTag category={g.category} />
                <PriorityBadge priority={g.priority} />
              </div>
              <div className={"mt-3 flex items-center justify-between text-[11px] " + t.textFaint}>
                <span>Filed {fmtDate(g.filedDate)}</span>
                <span>{g.status === "resolved" ? "Closed" : `${g.daysPending}d pending`}</span>
              </div>
              <div className={"mt-1.5 text-[11px] " + (g.assignedTo === "Unassigned" ? t.textFaint + " italic" : t.textFaint)}>
                Handler: {g.assignedTo}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[780px] border-collapse text-sm">
        <thead>
          <tr className={"text-left text-[11px] font-semibold uppercase tracking-wide " + t.textFaint}>
            <th className="px-3 py-2.5">Grievance ID</th>
            <th className="px-3 py-2.5">Category</th>
            <th className="px-3 py-2.5">Status</th>
            <th className="px-3 py-2.5">Priority</th>
            <th className="px-3 py-2.5">Filed</th>
            <th className="px-3 py-2.5">Days Pending</th>
            <th className="px-3 py-2.5">Handler</th>
            <th className="px-3 py-2.5 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => {
            const keyId = g.id || g.grievanceId;
            const isHighlighted = highlightedTest === keyId;
            return (
              <tr
                key={keyId}
                ref={(el) => {
                  if (el) rowRefs.current.set(keyId, el);
                }}
                className={
                  "border-t transition-all duration-300 " +
                  t.rowBorder + " " + t.rowHover +
                  (isHighlighted ? " ta-highlight-flash" : "")
                }
              >
                <td className={"px-3 py-3 font-bold " + t.textPrimary}>{g.id}</td>
                <td className="px-3 py-3"><CategoryTag category={g.category} /></td>
                <td className="py-3 min-w-[120px]"><StatusBadge status={g.status} size="sm" /></td>
                <td className="px-3 py-3"><PriorityBadge priority={g.priority} /></td>
                <td className={"px-3 py-3 " + t.textMuted}>{fmtDate(g.filedDate)}</td>
                <td className={"px-3 py-3 " + t.textMuted}>{g.status === "resolved" ? "—" : `${g.daysPending} days`}</td>
                <td className={"px-3 py-3 " + (g.assignedTo === "Unassigned" ? t.textFaint + " italic" : t.textMuted)}>{g.assignedTo}</td>
                <td className="px-3 py-3 text-right">
                  <button
                    onClick={() => onOpen(g.id)}
                    className="rounded-full px-4 py-1.5 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5"
                    style={{
                      background: BRAND.gradient,
                      boxShadow: "0 10px 22px -10px rgba(239,93,168,0.5)",
                    }}
                  >
                    View Case
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export — Grievances dashboard
// ---------------------------------------------------------------------------

export default function GrievancesDashboard() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { code, token } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    escalated: 0,
    inProgress: 0,
    resolvedThisMonth: 0,
  });
  const [analytics, setAnalytics] = useState({
    resolutionRate: 0,
    avgResolutionDays: 0,
  });
  const [filterCounts, setFilterCounts] = useState({ all: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [activeFilter, setActiveFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);

  const showToast = useCallback(
    (message, type = "success") => setToast({ message, type }),
    [],
  );
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  // Debounce free-text search before it hits the API.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const listQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (code) params.set("code", code);
    if (activeFilter !== "all") params.set("status", activeFilter);
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    return params.toString();
  }, [code, activeFilter, debouncedSearch, page]);

  // ---------------------------------------------------------------------
  // API — GET list + counts + stats + analytics in one call.
  // GET {serverUrl}api/v1/hr/grievances?code=&status=&search=&page=&limit=
  // ---------------------------------------------------------------------
  const fetchList = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(false);
      try {
        const data = await apiRequest(`api/v1/hr/grievances?${listQuery}`, {
          token,
        });
        setRows(data.grievances);
        setPagination(data.pagination);
        setStats(data.stats);
        setFilterCounts(data.filterCounts);
        setAnalytics(data.analytics);
      } catch (err) {
        console.error(err);
        setError(true);
        if (silent) showToast(err.message, "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [listQuery, token, showToast],
  );

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const changeFilter = (key) => {
    setActiveFilter(key);
    setPage(1);
  };

  // Instead of opening a popup, navigate to the case's own page. Adjust the
  // path below to match wherever this route is mounted in your router.
  const openCase = (id) => {
    navigate(`/grievances/${id}`);
  };

  return (
    <div
      className={
        "min-h-full w-full flex flex-col gap-5 sm:gap-6 " +
        t.textPrimary +
        " " +
        t.pageWash
      }
    >
      <div className="px-4 sm:px-6 md:px-10 pt-6 md:pt-10">
        <h1 className={"text-xl sm:text-2xl font-bold " + t.textPrimary}>
          Grievances
        </h1>
        <p className={"mt-1 flex items-center gap-1.5 text-sm " + t.textMuted}>
          <Lock size={13} />
          Anonymized, ID-based tracking for HR case management
        </p>
      </div>

      {/* 1. Dashboard statistics */}
      <div className="px-4 sm:px-6 md:px-10">
        <DashboardStats stats={stats} />
      </div>

      {/* 4. Analytics cards */}
      <div className="px-4 sm:px-6 md:px-10">
        <AnalyticsCards
          resolutionRate={analytics.resolutionRate}
          avgResolutionDays={analytics.avgResolutionDays}
        />
      </div>

      {/* 2 & 3. Filters + table */}
      <div className="px-4 sm:px-6 md:px-10">
        <CardShell accent="purple">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FilterIcon size={16} className="text-[#8b7cf6]" />
              <h2 className={"text-base sm:text-lg font-bold " + t.textPrimary}>
                All Grievances
              </h2>
            </div>
            <button
              onClick={() => fetchList()}
              className={
                "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors " +
                t.pillBorder +
                " " +
                t.surfaceSoft +
                " " +
                t.surfaceHover +
                " " +
                t.textMuted
              }
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <FilterBar
            counts={filterCounts}
            active={activeFilter}
            onChange={changeFilter}
            search={searchInput}
            onSearch={setSearchInput}
          />

          <div className="mt-5">
            <GrievancesTable
              rows={rows}
              loading={loading}
              error={error}
              onOpen={openCase}
              setPage={setPage}
            />
          </div>

          {!loading && !error && (
            <Pagination pagination={pagination} onPageChange={setPage} />
          )}
        </CardShell>
      </div>

      {/* 6. Privacy note (footer, not a dashboard section) */}
      <div className="px-4 sm:px-6 md:px-10 pb-8">
        <div
          className={
            "flex items-start gap-2.5 rounded-2xl px-4 py-3 text-[11px] sm:text-xs leading-relaxed " +
            t.surfaceSoft +
            " " +
            t.textFaint
          }
        >
          <Lock size={14} className="mt-0.5 shrink-0" />
          Every grievance is tracked only by its unique ID — no names or other
          identifying details are stored or displayed here. Handlers can trace,
          update, and resolve a case end-to-end using that ID alone.
        </div>
      </div>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
